import { useState } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';
import ReactHtmlParser from 'react-html-parser';

import Container from '../../../Core/Container';
import RichText from '../../../Core/RichText';

import CalculatorBanking from './CalculatorBanking';
import CalculatorSpending from './CalculatorSpending';
import CalculatorSummary from './CalculatorSummary';
import {
  Intro,
  Header,
  CalculatorWrapper,
  Calculator,
  Results,
  CalculatorHeader,
  ButtonContainer,
  Button,
  CalculatorTitle,
} from './styles';

const header = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Header>{children}</Header>,
    [BLOCKS.HEADING_1]: (_, children) => <Header>{children}</Header>,
    [BLOCKS.HEADING_2]: (_, children) => <Header>{children}</Header>,
    [BLOCKS.HEADING_3]: (_, children) => <Header>{children}</Header>,
  },
};

const SavingCalculatorSection = ({ data }) => {
  if (!data) return null;

  const { sectionContainerHeading, bodyCopy, contentAlignment, elements } =
    data;

  const cleanString = (str, reverse = false) => {
    return !reverse ? str.split(' ').join('-') : str.split('-').join(' ');
  };

  const createFilterOption = ({ value, label }) => {
    return {
      value: cleanString(value),
      label: ReactHtmlParser(label),
      ariaLabel: label,
    };
  };

  const createFilterOptions = options =>
    options
      ? options.map(option =>
          createFilterOption({
            value: option.value,
            label: option.label,
          }),
        )
      : null;

  const [spendingType, setSpendingType] = useState(elements[0].component);
  const [estimatedSpending, setEstimatedSpending] = useState(0);
  const [estimatedBanking, setEstimatedBanking] = useState(0);

  // find calculator banking data
  const bankingData = elements.find(
    el => el.component === 'calculator-banking',
  );

  // find calculator spending data
  const spendingData = elements.find(
    el => el.component === 'calculator-spending',
  );

  const slidersSectionCombined = [
    ...bankingData.sliderSection,
    ...spendingData.sliderSection,
  ];

  const defaultSelectedCard =
    bankingData.defaultSelectedCard || spendingData.defaultSelectedCard;

  const dropDownSelections = slidersSectionCombined.find(
    slider => slider.inputType === 'dropdown',
  );

  const dropdownItems = dropDownSelections.dropdownOptions.map(
    ({ label, value }) => {
      return {
        value,
        label,
      };
    },
  );
  const dropdownOptions = createFilterOptions(dropdownItems);
  const defaultDropdownSelection = createFilterOption(dropdownItems[0]);

  const [selectedOption, setSelectedOption] = useState(
    defaultDropdownSelection,
  );

  const defaultState = activeCard => {
    const obj = {};
    const ptsObj = {};
    slidersSectionCombined.map(
      ({
        inputType,
        sliderLabel,
        ppdMastercard,
        ppdWorld,
        ppdWorldElite,
        ppdPcma,
        avgMonthlySpendingMastercard,
        avgMonthlySpendingWorld,
        avgMonthlySpendingWorldElite,
        avgMonthlySpendingPcma,
      }) => {
        const key = sliderLabel.split(' ').join('-').toLocaleLowerCase();
        if (inputType === 'input') {
          obj[key] = {
            value: 0,
            points: 0,
          };
        }
        if (inputType === 'dropdown') {
          obj[key] = {
            points: 0,
          };
        }
        if (inputType === 'slider') {
          ptsObj[key] = {
            'pc-financial-mastercard': avgMonthlySpendingMastercard || 0,
            'pc-financial-world-mastercard': avgMonthlySpendingWorld || 0,
            'pc-financial-world-elite-mastercard':
              avgMonthlySpendingWorldElite || 0,
            'pc-money-account': avgMonthlySpendingPcma || 0,
          };
          obj[key] = {
            'pc-financial-mastercard': ppdMastercard,
            'pc-financial-world-mastercard': ppdWorld,
            'pc-financial-world-elite-mastercard': ppdWorldElite,
            'pc-money-account': ppdPcma,
            value: ptsObj[key][activeCard],
            points: 0,
          };
        }
        return true;
      },
    );
    return obj;
  };

  const [calculatorValues, setCalculatorValues] = useState(
    defaultState(defaultSelectedCard),
  );

  const summarizedBankData = {
    ...bankingData,
    estimatedValue: estimatedBanking,
  };
  const summarizedSpendingData = {
    ...spendingData,
    estimatedValue: estimatedSpending,
  };

  const summaryData = {
    spending: estimatedSpending,
    banking: estimatedBanking,
  };

  // find product names and attach related value
  const productLists = {
    productLists: [summarizedBankData, summarizedSpendingData]
      .map(el =>
        el.products.map(prod => {
          return { ...prod, ...{ estimatedValue: el.estimatedValue } };
        }),
      )
      .flat(),
  };

  // set up final data streams
  const bankingFinalData = { ...bankingData, ...summaryData, ...productLists };
  const spendingFinalData = {
    ...spendingData,
    ...summaryData,
    ...productLists,
  };

  const selectedData =
    spendingType === 'calculator-banking'
      ? bankingFinalData
      : spendingFinalData;

  const handleTabButtonClick = e => {
    window.dataLayer = window.dataLayer || [];
    const gtmData = {
      event: 'pco calculator engagement', // cta click event name
      action:
        e.currentTarget.value === 'calculator-banking'
          ? 'banking view'
          : 'spending view',
    };
    window.dataLayer.push(gtmData);
    setSpendingType(e.currentTarget.value);
  };

  return (
    <Container className="saving-calculator">
      <Intro contentAlignment={contentAlignment}>
        {sectionContainerHeading && (
          <RichText document={sectionContainerHeading} options={header} />
        )}
        {bodyCopy && <RichText document={bodyCopy} />}
      </Intro>
      <CalculatorWrapper>
        <Calculator>
          <CalculatorHeader>
            <ButtonContainer>
              {elements.map(prod => {
                return (
                  <Button
                    key={prod.id}
                    type="button"
                    value={prod.component}
                    onClick={handleTabButtonClick}
                    className={`${
                      spendingType === prod.component ? 'active' : 'inactive'
                    }`}
                  >
                    <RichText document={prod.products[0].text} />
                  </Button>
                );
              })}
            </ButtonContainer>
            <CalculatorTitle>
              <RichText document={selectedData.heading} />
            </CalculatorTitle>
          </CalculatorHeader>
          {spendingType === 'calculator-banking' ? (
            <CalculatorBanking
              setEstimatedBanking={setEstimatedBanking}
              setCalculatorValues={setCalculatorValues}
              setSelectedOption={setSelectedOption}
              selectedOption={selectedOption}
              calculatorValues={calculatorValues}
              dropdownOptions={dropdownOptions}
              heading={selectedData.heading}
              data={bankingData}
            />
          ) : (
            <CalculatorSpending
              setEstimatedSpending={setEstimatedSpending}
              setCalculatorValues={setCalculatorValues}
              calculatorValues={calculatorValues}
              heading={selectedData.heading}
              data={spendingData}
            />
          )}
        </Calculator>
        <Results>
          <CalculatorSummary data={selectedData} />
        </Results>
      </CalculatorWrapper>
    </Container>
  );
};

SavingCalculatorSection.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default SavingCalculatorSection;
